import { AxiosError } from 'axios';

import { parseArrayBuffer } from '@/core/lib/ArrayBufferUtils';
import { isRecord, isStringOrUndefined } from '@/core/lib/AssertUtils';

interface ApiErrorCause {
  message?: string;
  champ?: string;
}

const parseResponse = (axiosError: AxiosError): Record<string, unknown> | undefined => {
  const responseRaw = axiosError.response?.data;
  const responseParsed = responseRaw instanceof ArrayBuffer ? parseArrayBuffer(responseRaw) : responseRaw;

  return responseParsed && isRecord(responseParsed) ? responseParsed : undefined;
};

const getResponseType = (response: Record<string, unknown>): string | undefined => {
  return isStringOrUndefined(response.type) ? response.type : undefined;
};

const getResponseMessage = (response: Record<string, unknown>): string | undefined => {
  return isStringOrUndefined(response.message) ? response.message : undefined;
};

const getResponseCauses = (response: Record<string, unknown>): ApiErrorCause[] => {
  if (!Array.isArray(response.causes)) {
    return [];
  }

  const causes: ApiErrorCause[] = [];

  for (const cause of response.causes) {
    if (!isRecord(cause)) {
      break;
    }

    const message: string | undefined = isStringOrUndefined(cause.message) ? cause.message : undefined;
    const champ: string | undefined = isStringOrUndefined(cause.champ) ? cause.champ : undefined;

    if (message || champ) {
      causes.push({ message, champ });
    }
  }

  return causes;
};

class ApiError extends Error {
  readonly #axiosError: AxiosError;

  readonly #type?: string;

  readonly #causes?: ApiErrorCause[];

  constructor(axiosError: AxiosError) {
    const response = parseResponse(axiosError);
    const businessMessage = response ? getResponseMessage(response) : undefined;

    // On ne fallback jamais ici sur `axiosError.message` : si aucun message métier n'est
    // présent dans la réponse, `message` reste vide afin que les appelants (toastError, etc.)
    // puissent retomber sur leur propre message de repli plutôt que d'afficher le texte axios brut.
    super(businessMessage ?? '');

    this.#axiosError = axiosError;

    if (response) {
      this.#type = getResponseType(response);
      this.#causes = getResponseCauses(response);
    }
  }

  get status(): number | undefined {
    return this.#axiosError.response?.status;
  }

  get type(): string | undefined {
    return this.#type;
  }

  get causes(): ApiErrorCause[] {
    return this.#causes ?? [];
  }
}

export default ApiError;
